/**
 * AdminBillsEnhanced - Enhanced Vendor Bills list view with standardized table
 * 
 * Features:
 * - Server-side sorting on all columns
 * - Server-side filtering (status, vendor, date range, amount range)
 * - Global search
 * - Multi-select with bulk actions
 * - Pay Selected: Multi-bill payment for same vendor
 * - Admin-only delete with confirmation
 * - Stats summary cards
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  MonoCell,
  CurrencyCell,
  DateCell,
  StatusCell,
  BulkActionToolbar,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  DateRangeFilter,
  NumericRangeFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { vendorBillsAPI, vendorsAPI, paymentsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  FileText,
  Trash2,
  DollarSign,
  Clock,
  CheckCircle,
  Ban,
  AlertTriangle,
  Plus,
  CreditCard,
  Loader2,
  AlertCircle,
  X,
  Edit2,
} from 'lucide-react';

// ===================================================
// CONSTANTS
// ===================================================

const BILL_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  { value: 'unpaid', label: 'Unpaid', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'void', label: 'Void', color: 'bg-gray-100 text-gray-500', icon: Ban },
];

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const STATUS_CONFIG = BILL_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

// ===================================================
// HELPER FUNCTIONS
// ===================================================

const formatCents = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// ===================================================
// STATS CARDS COMPONENT
// ===================================================

const StatsCards = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-4">
              <div className="h-4 bg-ivory-200 rounded w-20 mb-2" />
              <div className="h-8 bg-ivory-200 rounded w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const unpaid = stats.unpaid || { count: 0, balance: 0 };
  const paid = stats.paid || { count: 0, amount: 0 };
  const overdue = stats.overdue || { count: 0, balance: 0 };

  const cards = [
    { label: 'Unpaid Bills', value: unpaid.count || 0, icon: Clock, color: 'text-yellow-600' },
    { label: 'Unpaid Amount', value: formatCents(unpaid.balance || 0), icon: DollarSign, color: 'text-yellow-600' },
    { label: 'Overdue', value: `${overdue.count || 0} bills`, icon: AlertTriangle, color: 'text-red-600' },
    { label: 'Paid (Total)', value: formatCents(paid.amount || 0), icon: CheckCircle, color: 'text-green-600' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {cards.map((card, i) => (
        <Card key={i}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-ivory-500 mb-1">
              <card.icon className={`w-4 h-4 ${card.color}`} />
              {card.label}
            </div>
            <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ===================================================
// MAIN COMPONENT
// ===================================================

const AdminBillsEnhanced = ({ onViewBill, onCreateBill }) => {
  const { isAdmin } = useAuth();
  
  // Vendors for filtering
  const [vendors, setVendors] = useState([]);
  
  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Bulk action modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Pay Selected Modal State
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    payment_reference: '',
    amount_cents: '',
    memo: ''
  });
  const [allocations, setAllocations] = useState([]);
  const [billsToPayData, setBillsToPayData] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Load vendors for filter dropdown
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const data = await vendorsAPI.getVendors({ is_active: true, page_size: 200 });
        setVendors(data.vendors || []);
      } catch (err) {
        console.error('Failed to load vendors:', err);
      }
    };
    loadVendors();
  }, []);
  
  // Fetch function for bills
  const fetchBills = useCallback(async (params) => {
    const response = await vendorBillsAPI.getBills(params);
    // Update stats from response
    if (response.stats) {
      setStats(response.stats);
      setStatsLoading(false);
    }
    return response;
  }, []);
  
  // Use admin table hook
  const {
    data: bills,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchBills,
    defaultSortBy: 'due_date',
    defaultSortDir: 'asc',
    defaultPageSize: 25,
    filterKeys: ['status', 'vendor_id', 'due_from', 'due_to', 'amount_min', 'amount_max'],
    transformResponse: (data) => ({
      items: data.bills || data.items || [],
      total: data.total || 0
    })
  });
  
  // Get selected bills for validation
  const selectedBills = bills.filter(bill => selectedIds.includes(bill.id));
  
  // Calculate blocked items for delete
  const getBlockedDeleteItems = () => {
    return selectedBills.filter(bill => {
      const status = (bill.status || '').toLowerCase();
      return ['paid', 'void'].includes(status) || (bill.amount_paid_cents || 0) > 0;
    }).map(bill => ({
      id: bill.id,
      reason: `Bill ${bill.bill_number} is ${bill.status || 'has payments'}`
    }));
  };
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await vendorBillsAPI.bulkDelete({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} bill(s)`);
      }
      if (result.blocked_items?.length > 0) {
        toast.warning(`${result.blocked_items.length} bill(s) could not be deleted`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete bills');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // ===================================================
  // PAY SELECTED FUNCTIONALITY
  // ===================================================
  
  // Validate selected bills for payment
  const paySelectedValidation = useMemo(() => {
    if (selectedBills.length === 0) {
      return { valid: false, reason: 'No bills selected' };
    }
    
    // Check for invalid statuses
    const invalidBills = selectedBills.filter(b => b.status === 'paid' || b.status === 'void');
    if (invalidBills.length > 0) {
      return { valid: false, reason: 'Cannot pay paid or voided bills' };
    }
    
    // Check for zero balance
    const zeroBalance = selectedBills.filter(b => (b.balance_cents || 0) <= 0);
    if (zeroBalance.length > 0) {
      return { valid: false, reason: 'Selected bills have zero balance' };
    }
    
    // Check same vendor
    const vendorIds = new Set(selectedBills.map(b => b.vendor_id));
    if (vendorIds.size > 1) {
      return { valid: false, reason: 'Select bills from the same vendor to pay together' };
    }
    
    return { valid: true, totalBalance: selectedBills.reduce((sum, b) => sum + (b.balance_cents || 0), 0) };
  }, [selectedBills]);
  
  // Open Pay Modal
  const openPayModal = async () => {
    if (!paySelectedValidation.valid) return;
    
    try {
      // Fetch full bill data
      const res = await vendorBillsAPI.batchFetch(Array.from(selectedIds));
      const billsData = (res.bills || []).sort((a, b) => {
        // Sort by due date (oldest first)
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
      
      setBillsToPayData(billsData);
      
      // Initialize allocations with full balance
      setAllocations(billsData.map(b => ({
        bill_id: b.id,
        bill_number: b.bill_number,
        due_date: b.due_date,
        balance_cents: b.balance_cents,
        amount_cents: b.balance_cents
      })));
      
      // Set default payment amount to total balance
      const totalBalance = billsData.reduce((sum, b) => sum + b.balance_cents, 0);
      setPaymentData(prev => ({
        ...prev,
        amount_cents: (totalBalance / 100).toFixed(2)
      }));
      
      setShowPayModal(true);
    } catch (err) {
      toast.error(err.message || 'Failed to load bills');
    }
  };
  
  // Auto-allocate payment
  const autoAllocate = () => {
    const paymentCents = Math.round(parseFloat(paymentData.amount_cents || 0) * 100);
    let remaining = paymentCents;
    
    const newAllocations = billsToPayData.map(b => {
      const toApply = Math.min(remaining, b.balance_cents);
      remaining -= toApply;
      return {
        bill_id: b.id,
        bill_number: b.bill_number,
        due_date: b.due_date,
        balance_cents: b.balance_cents,
        amount_cents: toApply
      };
    });
    
    setAllocations(newAllocations);
  };
  
  // Update allocation for a specific bill
  const updateAllocation = (billId, value) => {
    const cents = Math.round(parseFloat(value || 0) * 100);
    setAllocations(prev => prev.map(a => 
      a.bill_id === billId 
        ? { ...a, amount_cents: Math.min(cents, a.balance_cents) }
        : a
    ));
  };
  
  // Calculate totals
  const allocatedTotal = useMemo(() => {
    return allocations.reduce((sum, a) => sum + a.amount_cents, 0);
  }, [allocations]);
  
  const paymentAmountCents = useMemo(() => {
    return Math.round(parseFloat(paymentData.amount_cents || 0) * 100);
  }, [paymentData.amount_cents]);
  
  const remainingUnallocated = paymentAmountCents - allocatedTotal;
  
  // Submit payment
  const handleSubmitPayment = async () => {
    if (allocatedTotal <= 0) {
      toast.error('No amounts allocated');
      return;
    }
    if (allocatedTotal > paymentAmountCents) {
      toast.error('Allocated amount exceeds payment amount');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const vendorId = billsToPayData[0]?.vendor_id;
      
      const applications = allocations
        .filter(a => a.amount_cents > 0)
        .map(a => ({
          applies_to_type: 'vendor_bill',
          applies_to_id: a.bill_id,
          applied_amount_cents: a.amount_cents
        }));
      
      await paymentsAPI.createPayment({
        payment_type: 'expense',
        vendor_id: vendorId,
        payment_date: paymentData.payment_date,
        payment_method: paymentData.payment_method,
        payment_reference: paymentData.payment_reference,
        amount_cents: paymentAmountCents,
        memo: paymentData.memo,
        applications
      });
      
      toast.success('Payment recorded successfully');
      setShowPayModal(false);
      clearSelection();
      setPaymentData({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        payment_reference: '',
        amount_cents: '',
        memo: ''
      });
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to create payment');
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Build vendor options for filter
  const vendorOptions = vendors.map(v => ({
    value: v.id,
    label: v.name
  }));
  
  // Table columns definition
  const columns = [
    {
      key: 'bill_number',
      header: 'Bill #',
      sortable: true,
      render: (value, row) => (
        <MonoCell 
          value={value} 
          onClick={() => onViewBill?.(row.id)}
          className="font-semibold"
        />
      )
    },
    {
      key: 'vendor_name',
      header: 'Vendor',
      sortable: false,
      render: (value) => (
        <span className="font-medium text-ivory-800 truncate block max-w-[180px]" title={value}>
          {value || '—'}
        </span>
      )
    },
    {
      key: 'po_number',
      header: 'PO #',
      sortable: false,
      render: (value) => (
        <MonoCell value={value} className="text-xs text-ivory-500" />
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => <StatusCell value={value} statusConfig={STATUS_CONFIG} />
    },
    {
      key: 'amount_cents',
      header: 'Amount',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="text-right block font-medium tabular-nums">
          {formatCents(value)}
        </span>
      )
    },
    {
      key: 'balance_cents',
      header: 'Balance',
      sortable: true,
      align: 'right',
      render: (value, row) => {
        const hasBalance = value > 0;
        return (
          <span className={`text-right block font-medium tabular-nums ${hasBalance ? 'text-orange-600' : 'text-green-600'}`}>
            {formatCents(value)}
          </span>
        );
      }
    },
    {
      key: 'bill_date',
      header: 'Bill Date',
      sortable: true,
      render: (value) => <DateCell value={value} />
    },
    {
      key: 'due_date',
      header: 'Due Date',
      sortable: true,
      render: (value, row) => {
        const isOverdue = value && new Date(value) < new Date() && row.status !== 'paid' && row.status !== 'void';
        return (
          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
            <DateCell value={value} />
          </span>
        );
      }
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewBill?.(row.id);
            }}
            title="View Details"
            data-testid={`view-bill-${row.bill_number}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];
  
  // Filter labels for chips
  const filterLabels = {
    status: 'Status',
    vendor_id: 'Vendor',
    due_from: 'Due From',
    due_to: 'Due To',
    amount_min: 'Min Amount',
    amount_max: 'Max Amount',
  };
  
  // Format filter values for display
  const formatFilterValue = (key, value) => {
    if (key === 'vendor_id') {
      const vendor = vendors.find(v => v.id === value);
      return vendor?.name || value;
    }
    if (key === 'status') {
      const status = BILL_STATUSES.find(s => s.value === value);
      return status?.label || value;
    }
    return value;
  };
  
  return (
    <div className="space-y-6" data-testid="admin-bills-enhanced">
      {/* Stats Cards */}
      <StatsCards stats={stats} loading={statsLoading} />
      
      {/* Header */}
      <AdminListHeader
        title="Bills to Pay"
        subtitle={total > 0 ? `${total.toLocaleString()} total bills` : undefined}
        createLabel="Create Bill"
        onCreate={onCreateBill}
        canCreate={isAdmin}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
      />
      
      {/* Filters */}
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search bill #, memo..."
          className="flex-1 min-w-[250px]"
        />
        
        <StatusFilter
          value={filters.status}
          onChange={(v) => setFilter('status', v)}
          options={BILL_STATUSES}
          placeholder="All Statuses"
          className="w-[150px]"
          data-testid="bill-status-filter"
        />
        
        <StatusFilter
          value={filters.vendor_id}
          onChange={(v) => setFilter('vendor_id', v)}
          options={vendorOptions}
          placeholder="All Vendors"
          className="w-[180px]"
          data-testid="bill-vendor-filter"
        />
        
        <DateRangeFilter
          fromValue={filters.due_from}
          toValue={filters.due_to}
          onFromChange={(v) => setFilter('due_from', v)}
          onToChange={(v) => setFilter('due_to', v)}
          label="Due Date"
          className="w-[180px]"
        />
        
        <NumericRangeFilter
          minValue={filters.amount_min}
          maxValue={filters.amount_max}
          onMinChange={(v) => setFilter('amount_min', v)}
          onMaxChange={(v) => setFilter('amount_max', v)}
          label="Amount"
          prefix="$"
          className="w-[180px]"
        />
      </FilterBar>
      
      {/* Active filter chips */}
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
        formatValue={formatFilterValue}
      />
      
      {/* Pay Selected Warning */}
      {selectedCount > 0 && !paySelectedValidation.valid && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2 text-yellow-800 text-sm">
          <AlertCircle className="w-4 h-4" />
          {paySelectedValidation.reason}
        </div>
      )}
      
      {/* Bulk action toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        <Button
          variant={paySelectedValidation.valid ? 'default' : 'outline'}
          size="sm"
          disabled={!paySelectedValidation.valid}
          onClick={openPayModal}
          data-testid="pay-selected-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Pay Selected
        </Button>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {/* Data Table */}
      <AdminDataTable
        tableId="bills"
        data={bills}
        columns={columns}
        loading={loading}
        emptyMessage="No bills found"
        emptyIcon={FileText}
        
        // Selection
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.id}
        
        // Sorting
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        
        // Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        
        // Density
        density={density}
        onDensityChange={setDensity}
        
        // Column preferences
        showColumnSelector={true}
        resizableColumns={true}
        
        // Row click opens detail
        onRowClick={(row) => onViewBill?.(row.id)}
        
        data-testid="bills-data-table"
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="bill"
        loading={bulkActionLoading}
        blockedItems={getBlockedDeleteItems()}
        warnings={[
          'Soft-deleted bills can be restored by an administrator.',
          'PAID and VOID bills cannot be deleted.',
          'Bills with payments applied cannot be deleted.'
        ]}
        requireReason={true}
      />
      
      {/* Pay Selected Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-3xl my-8" data-testid="pay-bills-modal">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold">Pay Bills</h3>
                <p className="text-gray-500 text-sm">
                  Vendor: {billsToPayData[0]?.vendor_name}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPayModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Payment Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <Input
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    data-testid="payment-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                  <select
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    data-testid="payment-method-select"
                  >
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference #</label>
                  <Input
                    value={paymentData.payment_reference}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
                    placeholder="Check #, ACH ref..."
                    data-testid="payment-reference-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={paymentData.amount_cents}
                    onChange={(e) => setPaymentData({ ...paymentData, amount_cents: e.target.value })}
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={autoAllocate}
                    data-testid="auto-allocate-btn"
                  >
                    Auto-Allocate
                  </Button>
                </div>
              </div>
              
              {/* Bills to Pay */}
              <div>
                <h4 className="font-medium mb-3">Allocate to Bills</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="allocation-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Bill #</th>
                        <th className="px-3 py-2 text-left font-medium">Due Date</th>
                        <th className="px-3 py-2 text-right font-medium">Balance</th>
                        <th className="px-3 py-2 text-right font-medium w-32">Apply ($)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allocations.map((alloc) => (
                        <tr key={alloc.bill_id}>
                          <td className="px-3 py-2">{alloc.bill_number || alloc.bill_id.slice(0, 8)}</td>
                          <td className="px-3 py-2">{alloc.due_date || '—'}</td>
                          <td className="px-3 py-2 text-right">{formatCents(alloc.balance_cents)}</td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max={(alloc.balance_cents / 100).toFixed(2)}
                              value={(alloc.amount_cents / 100).toFixed(2)}
                              onChange={(e) => updateAllocation(alloc.bill_id, e.target.value)}
                              className="h-8 text-sm text-right"
                              data-testid={`alloc-${alloc.bill_id}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={2}></td>
                        <td className="px-3 py-2 text-right font-medium">Total Applied:</td>
                        <td className="px-3 py-2 text-right font-medium">{formatCents(allocatedTotal)}</td>
                      </tr>
                      <tr>
                        <td colSpan={2}></td>
                        <td className="px-3 py-2 text-right font-medium">Payment Amount:</td>
                        <td className="px-3 py-2 text-right font-medium">{formatCents(paymentAmountCents)}</td>
                      </tr>
                      <tr>
                        <td colSpan={2}></td>
                        <td className="px-3 py-2 text-right font-medium">Remaining:</td>
                        <td className={`px-3 py-2 text-right font-medium ${remainingUnallocated < 0 ? 'text-red-600' : ''}`}>
                          {formatCents(remainingUnallocated)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              
              {remainingUnallocated < 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-800 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Allocated amount exceeds payment amount
                </div>
              )}
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowPayModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitPayment}
                disabled={processingPayment || allocatedTotal <= 0 || remainingUnallocated < 0}
                data-testid="submit-payment-btn"
              >
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Record Payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBillsEnhanced;
